import { useMemo } from 'react'
import { useGetPositions } from '@/service-library/hooks/positions'
import { useGetPositionLookups } from '@/services/hooks/relay/positions'
import { Position } from '@/types/positions'
import { findLookup, summarizePositionRule } from '@/utils/positions'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

export type PolicyPositionOption = {
  position: Position
  ruleSummary: string
  lookupLabel: string
}

type UsePolicyPositionOptionsOptions = {
  enabled?: boolean
}

/**
 * This project's positions, ready to be offered as approvers when building
 * one of its policies. Positions are scoped to a project, so the same
 * position name can mean something different on another project's policies.
 *
 * The ERP's lookups are only used here for labels, so a project whose
 * accounting system can't be reached still offers every position it already
 * has set up.
 */
export default function usePolicyPositionOptions({
  enabled = true,
}: UsePolicyPositionOptionsOptions = {}) {
  const { project } = useProjectContext()

  const { positions, isPending } = useGetPositions({
    filters: { limit: '1000', project_id: project.id },
    enabled,
  })

  const { positionLookups } = useGetPositionLookups({
    orgCredentialTypeId: project.org_credential_type_id || '',
    enabled,
  })

  const positionOptions = useMemo(
    () =>
      positions.map((position): PolicyPositionOption => {
        const lookup = findLookup(positionLookups, position.lookup_code)

        return {
          position,
          ruleSummary: summarizePositionRule(position, lookup),
          lookupLabel: lookup?.name || position.lookup_code,
        }
      }),
    [positions, positionLookups],
  )

  const positionOptionsById = useMemo(
    () =>
      new Map(positionOptions.map((option) => [option.position.id, option])),
    [positionOptions],
  )

  return {
    positionOptions,
    positionOptionsById,
    isPending: enabled && isPending,
  }
}
